import { Component, Input, Output, EventEmitter, OnInit, OnChanges, HostListener, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ApiVersion } from '../../models/api-catalog.models';

/**
 * Version Selector Component
 * 
 * A standalone dropdown component for selecting API versions.
 * Displays the current version and allows switching between available versions.
 */
@Component({
  selector: 'app-version-selector',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './version-selector.component.html',
  styleUrls: ['./version-selector.component.scss']
})
export class VersionSelectorComponent implements OnInit, OnChanges {
  /**
   * Array of available versions to display in the dropdown
   */
  @Input() versions: ApiVersion[] = [];
  
  /**
   * Currently selected version ID (apiId)
   */
  @Input() selectedVersionId: string = '';
  
  /**
   * Event emitted when a version is selected
   */
  @Output() versionChange = new EventEmitter<ApiVersion>();
  
  /**
   * Flag to control dropdown visibility
   */
  isOpen = false;
  
  /**
   * Currently selected version object
   */
  selectedVersion: ApiVersion | undefined;
  
  constructor(private elementRef: ElementRef) {}
  
  ngOnInit(): void {
    this.updateSelectedVersion();
  }
  
  ngOnChanges(): void {
    this.updateSelectedVersion();
  }
  
  /**
   * Update the selected version based on selectedVersionId
   * @private
   */
  private updateSelectedVersion(): void {
    this.selectedVersion = this.versions.find(v => v.apiId === this.selectedVersionId);
    
    // If no version found and versions exist, select the first (default) one
    if (!this.selectedVersion && this.versions.length > 0) {
      this.selectedVersion = this.versions[0];
      this.selectedVersionId = this.selectedVersion.apiId;
    }
  }
  
  /**
   * Toggle dropdown visibility
   */
  toggleDropdown(): void {
    this.isOpen = !this.isOpen;
  }
  
  /**
   * Select a version and emit the change event
   * @param version - The version to select
   */
  selectVersion(version: ApiVersion): void {
    if (this.selectedVersion?.apiId === version.apiId) {
      // Same version selected, just close dropdown
      this.isOpen = false;
      return;
    }
    
    this.selectedVersion = version;
    this.selectedVersionId = version.apiId;
    this.isOpen = false;
    this.versionChange.emit(version);
  }
  
  /**
   * Close dropdown when clicking outside
   * @param event - Mouse event
   */
  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }
  
  /**
   * Get display label for a version
   * @param version - The version object
   * @returns Display string for the version
   */
  getVersionLabel(version: ApiVersion): string {
    return version.isDefault ? `${version.version} (Latest)` : version.version;
  }
  
  /**
   * Stop event propagation to prevent parent click handlers
   * @param event - Mouse event
   */
  stopPropagation(event: MouseEvent): void {
    event.stopPropagation();
  }
}
